import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    model,
    OnChanges,
    Output,
    SimpleChanges,
    ChangeDetectionStrategy
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { NoteView, SearchNotesParameters, SearchUsersParameters, UserView } from '@api-proxies';
import { NoteService } from '@http/clients/note.service';
import { UserService } from '@http/clients/user.service';
import { AwaiterService } from '@services/awaiter.service';
import {
    DatepickerDropdownComponent,
    IconSanitizerService,
    ITabPaginationConfig,
    ModalService,
    NoDataComponent,
    SearchInputComponent,
    TabItemComponent,
    TabViewComponent
} from 'ngx-lib';
import { CustomMenuItemComponent } from '../custom-menu-item/custom-menu-item.component';
import { GenericTypeAheadComponent, ITypeAheadService } from '../generic-type-ahead/generic-type-ahead.component';
import { ModalEditNoteComponent } from '../modals/modal-edit-note/modal-edit-note.component';
import { INoteEdit, NoteContentComponent } from './note-content/note-content.component';

interface IFilterValueNotes {
    searchText: string;
    user?: UserView;
    noteUser?: UserView;
    dateFrom?: Date;
    dateTo?: Date;
}

@Component({
    selector: 'app-notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        SearchInputComponent,
        DatepickerDropdownComponent,
        NoDataComponent,
        TabViewComponent,
        TabItemComponent,
        GenericTypeAheadComponent,
        CustomMenuItemComponent,
        NoteContentComponent
    ]
})
export class NotesComponent implements OnChanges {
    @Input()
    entityTypeId?: number;

    @Input()
    entityId?: number;

    @Input()
    entityName?: string;

    @Input()
    organizationId?: number;

    @Output()
    tabCounterChange: EventEmitter<void>;

    notes?: NoteView[];

    readonly currentTab = model('');

    paginationConfig: ITabPaginationConfig;

    searchConfig: SearchNotesParameters;

    filterValue: IFilterValueNotes;

    userServiceFilter: ITypeAheadService<UserView, SearchUsersParameters>;

    plusIcon?: SafeHtml;

    constructor(
        private readonly noteService: NoteService,
        private readonly modalService: ModalService,
        private readonly awaiter: AwaiterService,
        private readonly userService: UserService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        this.notes = [];
        this.paginationConfig = {
            pagesCount: 0,
            totalCount: 0,
            currentPage: 1
        };

        this.searchConfig = {
            pageNumber: this.paginationConfig.currentPage
        } as SearchNotesParameters;

        this.filterValue = {
            searchText: ''
        };

        this.userServiceFilter = userService;
        this.tabCounterChange = new EventEmitter<void>();
        this.plusIcon = this.iconSanitizer.getIcon('plus');
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['entityId'] || changes['entityTypeId']) {
            if (this.entityId) {
                this.refreshTabView();
            }
        }
    }

    async refreshTabView(): Promise<void> {
        this.buildSearchConfig();

        await this.awaiter.awaitAction('Loading Notes', async () => {
            const response = await this.noteService.search(this.searchConfig);
            const { pageInfo, results } = response;
            this.notes = this.notes?.concat(results ?? []);

            this.paginationConfig.pagesCount = pageInfo?.totalPages ?? 0;
            this.paginationConfig.currentPage = pageInfo?.pageNumber ?? 1;
            this.paginationConfig.totalCount = pageInfo?.itemsCount ?? 0;

            this.currentTab.set(this.notes?.length ? (this.notes[0].noteKey ?? '') : '');
        });
    }

    async onFilterChanged(): Promise<void> {
        this.buildFilters();
        this.resetTabList();
        await this.refreshTabView();
    }

    resetTabList(): void {
        this.paginationConfig.currentPage = 1;
        this.notes = [];
    }

    buildSearchConfig(): void {
        this.searchConfig = {
            pageNumber: this.paginationConfig.currentPage ?? 1,
            filterText: this.filterValue.searchText,
            createdByUserId: this.filterValue.user?.id,
            dateTo: this.filterValue.dateTo,
            dateFrom: this.filterValue.dateFrom,
            noteUsers: this.filterValue.noteUser?.id && [this.filterValue.noteUser?.id],
            entityTypeId: this.entityTypeId,
            entityId: this.entityId
        } as SearchNotesParameters;
    }

    buildFilters(): void {
        this.filterValue = {
            user: this.filterValue?.user,
            dateFrom: this.filterValue.dateFrom,
            dateTo: this.filterValue.dateTo,
            searchText: this.filterValue.searchText,
            noteUser: this.filterValue.noteUser
        };
    }

    async createNote(): Promise<void> {
        if (this.entityId && this.entityTypeId) {
            const note = await this.modalService.open(ModalEditNoteComponent, {
                entityId: this.entityId,
                entityTypeId: this.entityTypeId,
                notificationTitle: this.entityName,
                organizationId: this.organizationId
            });

            if (note && this.notes) {
                this.tabCounterChange.emit();
                this.notes?.unshift(note);
                this.currentTab.set('');
                setTimeout(() => {
                    this.currentTab.set(note.noteKey ?? '');
                }, 1);
            }
        }
    }

    onNoteChanged(note: INoteEdit): void {
        if (this.notes) {
            this.notes?.splice(note.index, 1);
            if (note.editedNote) {
                this.notes?.unshift(note.editedNote);
                this.currentTab.set('');
                setTimeout(() => {
                    this.currentTab.set(this.notes?.[0].noteKey ?? '');
                }, 1);
            } else {
                this.tabCounterChange.emit();

                if (this.notes.length > 0) {
                    if (note.index < this.notes.length) {
                        this.currentTab.set(this.notes[note.index].noteKey ?? '');
                    } else {
                        this.currentTab.set(this.notes[note.index - 1].noteKey ?? '');
                    }
                } else {
                    this.currentTab.set('');
                }
            }
        }
    }
}
